import { useCallback, useEffect, type FunctionComponent } from 'react';

import { AtlassianSwitcherPrefetchTrigger } from '@atlassian/switcher';
import { Analytics } from '@trello/atlassian-analytics';
import { getMemberIdWithFallback } from '@trello/authentication';
import { CrossFlowProvider } from '@trello/cross-flow';
import { HeaderButton } from '@trello/header-primitives';
import { intl } from '@trello/i18n';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import { useWaitOnQuery } from '@trello/quickload';
import { getTestId, type HeaderTestIds } from '@trello/test-ids';

import { AtlassianAppSwitcher } from './AtlassianAppSwitcher';
import { AtlassianAppSwitcherButtonDocument } from './AtlassianAppSwitcherButtonQuery.generated';
import { AtlassianAppSwitcherIcon } from './AtlassianAppSwitcherIcon';

import * as styles from './AtlassianAppSwitcherButton.module.less';

export const AtlassianAppSwitcherButtonUnconnected: FunctionComponent = () => {
  const memberId = getMemberIdWithFallback();
  const { data } = useWaitOnQuery(AtlassianAppSwitcherButtonDocument, {
    variables: { memberId },
    waitOn: ['MemberHeader'],
    context: {
      operationName: 'AtlassianAppSwitcherButton',
      document: AtlassianAppSwitcherButtonDocument,
    },
  });
  const member = data?.member;
  const locale = member?.prefs?.locale;
  const aaId = member?.aaId ?? undefined;

  const onShow = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianSwitcherHeaderButton',
      source: 'appHeader',
      attributes: {
        aaId,
        isAaMastered: true,
      },
    });
  }, [aaId]);

  const {
    popoverProps,
    triggerRef,
    toggle: togglePopover,
    hide,
  } = usePopover<HTMLButtonElement>({ onShow });

  useEffect(() => {
    // The AppSwitcher button is only mounted upon interaction (i.e. clicking), so we
    // want to immediately toggle the popover.
    togglePopover();
  }, [togglePopover]);

  const renderHeaderButton = useCallback(
    () => (
      <Tooltip
        content={intl.formatMessage({
          id: 'templates.cross_flow.more-from-atlassian',
          defaultMessage: 'More from Atlassian',
          description: 'Tooltip content for the Atlassian App Switcher button',
        })}
        position="bottom"
      >
        <HeaderButton
          icon={<AtlassianAppSwitcherIcon />}
          onClick={togglePopover}
          ref={triggerRef}
          testId={getTestId<HeaderTestIds>('atlassian-app-switcher')}
          ariaLabel={intl.formatMessage({
            id: 'templates.cross_flow.switch to',
            defaultMessage: 'Switch to…',
            description: 'Aria label for the Atlassian App Switcher button',
          })}
          className={styles.appSwitcher}
          isSelected={popoverProps.isVisible}
        />
      </Tooltip>
    ),
    [togglePopover, triggerRef, popoverProps.isVisible],
  );

  return (
    <>
      <AtlassianSwitcherPrefetchTrigger product="trello">
        {renderHeaderButton()}
      </AtlassianSwitcherPrefetchTrigger>
      <Popover
        {...popoverProps}
        testId={getTestId<HeaderTestIds>('atlassian-app-switcher')}
        noVerticalPadding
        noHorizontalPadding
        // The width is controlled by the switcher itself.
        // https://hello.atlassian.net/wiki/spaces/CXP/pages/3779494831/Standardizing+Switcher+Width?focusedCommentId=3779564723
        // Therefore, we need to set the parent width to 'auto.'
        // Since 'dangerous_width' must be a number, we ignore the type check to get around this
        // @ts-expect-error
        dangerous_width="auto"
      >
        <AtlassianAppSwitcher aaId={aaId} locale={locale} onClose={hide} />
      </Popover>
    </>
  );
};

export const AtlassianAppSwitcherButton: FunctionComponent = () => (
  <CrossFlowProvider>
    <AtlassianAppSwitcherButtonUnconnected />
  </CrossFlowProvider>
);
